import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  clampIso,
  todayIso,
  lastDayOfMonth,
  odbCyclesInRange,
  nihbCyclesInRange,
  formatPayDate,
} from "@/lib/dashboard-utils";

export function RangePicker({
  reportStart,
  reportEnd,
  monthsAvailable = [],
  initial,
  viewMode,
  onViewModeChange,
  onApply,
  onClear,
}) {
  const today = clampIso(todayIso(), reportStart, reportEnd) || reportEnd || "";
  const ym = (today || "").slice(0, 7);
  const fallbackStart = ym ? clampIso(`${ym}-01`, reportStart, reportEnd) : reportStart || "";
  const fallbackEnd = today;
  const [start, setStart] = useState(initial?.start || fallbackStart);
  const [end, setEnd] = useState(initial?.end || fallbackEnd);
  // Multi-select state for the ODB cycle dropdown. Tracks the set of
  // cycle keys the user has ticked. When the set is non-empty, the
  // Start/End fields show the encompassing range (min start → max end).
  // Set is cleared whenever the user picks any other preset or edits the
  // dates directly so the active cycles stay in sync with the actual range.
  //
  // SEEDED from `initial.odbCycleKeys` so that re-opening the picker after
  // an Apply preserves the ticked cycles — otherwise the user would see
  // no checkmarks and any subsequent NIHB toggle would silently DROP their
  // previously-picked ODB selection (recomputeRangeFromCycles reads
  // `selectedOdbKeys` from the closure and would treat it as empty).
  const [selectedOdbKeys, setSelectedOdbKeys] = useState(
    () => new Set(Array.isArray(initial?.odbCycleKeys) ? initial.odbCycleKeys : [])
  );
  // Same mechanism for NIHB cycles — bi-weekly windows that don't
  // align with ODB, so they need their own multi-select state.
  const [selectedNihbKeys, setSelectedNihbKeys] = useState(
    () => new Set(Array.isArray(initial?.nihbCycleKeys) ? initial.nihbCycleKeys : [])
  );
  // Same multi-select mechanism for the Month and Quarter dropdowns —
  // the user can tick several months/quarters and the range becomes the
  // min-start → max-end envelope of everything selected.
  const [selectedMonthKeys, setSelectedMonthKeys] = useState(() => new Set());
  const [selectedQuarterKeys, setSelectedQuarterKeys] = useState(() => new Set());

  // Re-clamp Start/End whenever the available report bounds change
  // (e.g., user toggles Product Scope and HCV vs Non-HCV have different dates).
  useEffect(() => {
    if (!reportStart || !reportEnd) return;
    setStart((prev) => {
      if (!prev || prev < reportStart || prev > reportEnd) return reportStart;
      return prev;
    });
    setEnd((prev) => {
      if (!prev || prev < reportStart || prev > reportEnd) return reportEnd;
      return prev;
    });
  }, [reportStart, reportEnd]);

  const invalid = !start || !end || start > end;
  const outOfBounds =
    (reportStart && start && start < reportStart) ||
    (reportEnd && end && end > reportEnd);

  const setRange = (s, e) => {
    setStart(clampIso(s, reportStart, reportEnd));
    setEnd(clampIso(e, reportStart, reportEnd));
    // Any range change from outside the multi-select toggles resets all
    // four Sets so checkmarks always reflect what's actually filtered.
    setSelectedOdbKeys(new Set());
    setSelectedNihbKeys(new Set());
    setSelectedMonthKeys(new Set());
    setSelectedQuarterKeys(new Set());
  };
  // Toggle a single ODB cycle in the multi-select. Encompassing range
  // becomes min(starts) → max(ends) across all currently ticked cycles.
  // Recompute the encompassing range from both ODB and NIHB selections
  // combined. ODB + NIHB can now be picked together (e.g. auditor wants
  // to see all deposit windows overlapping a period) so we no longer
  // clear one when the other changes.
  const recomputeRangeFromCycles = (odbSet, nihbSet, odbList, nihbList) => {
    const selected = [
      ...odbList.filter((c) => odbSet.has(c.key)),
      ...nihbList.filter((c) => nihbSet.has(c.key)),
    ];
    if (selected.length === 0) {
      setStart(reportStart);
      setEnd(reportEnd);
      return;
    }
    const starts = selected.map((c) => clampIso(c.dispenseStart, reportStart, reportEnd));
    const ends = selected.map((c) => clampIso(c.dispenseEnd, reportStart, reportEnd));
    setStart(starts.reduce((a, b) => (a < b ? a : b)));
    setEnd(ends.reduce((a, b) => (a > b ? a : b)));
  };

  const toggleOdbCycle = (cycle, cycleList) => {
    setSelectedOdbKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cycle.key)) next.delete(cycle.key);
      else next.add(cycle.key);
      recomputeRangeFromCycles(next, selectedNihbKeys, cycleList, nihbCycles);
      return next;
    });
    // Cycles override month/quarter (still mutually exclusive with those)
    // but ODB + NIHB coexist now.
    setSelectedMonthKeys(new Set());
    setSelectedQuarterKeys(new Set());
  };
  // Toggle a single NIHB cycle — identical mechanics to ODB but scoped
  // to the NIHB bi-weekly calendar. Coexists with ODB selections.
  const toggleNihbCycle = (cycle, cycleList) => {
    setSelectedNihbKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cycle.key)) next.delete(cycle.key);
      else next.add(cycle.key);
      recomputeRangeFromCycles(selectedOdbKeys, next, odbCycles, cycleList);
      return next;
    });
    setSelectedMonthKeys(new Set());
    setSelectedQuarterKeys(new Set());
  };
  // Toggle a month / quarter — same encompassing-range logic as ODB cycles.
  const toggleMonth = (ymKey) => {
    setSelectedMonthKeys((prev) => {
      const next = new Set(prev);
      if (next.has(ymKey)) next.delete(ymKey);
      else next.add(ymKey);
      if (next.size === 0) {
        setStart(reportStart);
        setEnd(reportEnd);
      } else {
        const keys = [...next];
        const starts = keys.map((k) => clampIso(`${k}-01`, reportStart, reportEnd));
        const ends = keys.map((k) => clampIso(lastDayOfMonth(k), reportStart, reportEnd));
        setStart(starts.reduce((a, b) => (a < b ? a : b)));
        setEnd(ends.reduce((a, b) => (a > b ? a : b)));
      }
      return next;
    });
    setSelectedOdbKeys(new Set());
    setSelectedNihbKeys(new Set());
    setSelectedQuarterKeys(new Set());
  };
  const toggleQuarter = (quarter, quarterList) => {
    setSelectedQuarterKeys((prev) => {
      const next = new Set(prev);
      if (next.has(quarter.key)) next.delete(quarter.key);
      else next.add(quarter.key);
      if (next.size === 0) {
        setStart(reportStart);
        setEnd(reportEnd);
      } else {
        const selected = quarterList.filter((q) => next.has(q.key));
        const starts = selected.map((q) => clampIso(q.start, reportStart, reportEnd));
        const ends = selected.map((q) => clampIso(q.end, reportStart, reportEnd));
        setStart(starts.reduce((a, b) => (a < b ? a : b)));
        setEnd(ends.reduce((a, b) => (a > b ? a : b)));
      }
      return next;
    });
    setSelectedOdbKeys(new Set());
    setSelectedNihbKeys(new Set());
    setSelectedMonthKeys(new Set());
  };
  const setMonth = (ymKey) => {
    setRange(`${ymKey}-01`, lastDayOfMonth(ymKey));
  };

  const todayActive = start === end && start && start !== "";
  const singleDayInputRef = useRef(null);
  const openSingleDayPicker = () => {
    // Eagerly set start=end=today so the button highlights green the moment
    // the user taps it — even before they choose a different day via the
    // native picker. This is what iOS users expect: an immediate visual
    // confirmation that the filter was applied.
    if (today) setRange(today, today);
    const input = singleDayInputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch {
        /* fall through to focus-based open */
      }
    }
    input.focus();
    input.click();
  };
  const fullActive = start === reportStart && end === reportEnd;
  // Month is "active" when start AND end land within the same calendar
  // month AND each side is either the natural calendar boundary OR clamped
  // to the report bound (a partial first/last month — e.g. May 4 → May 31
  // when the HCV report starts on May 4 — still counts as "May selected").
  const activeMonth = (() => {
    if (!start || !end) return null;
    const sYm = start.slice(0, 7);
    if (sYm !== end.slice(0, 7)) return null;
    const naturalStart = `${sYm}-01`;
    const naturalEnd = lastDayOfMonth(sYm);
    const startOk =
      start === naturalStart ||
      (start === reportStart && reportStart && reportStart <= naturalEnd);
    const endOk =
      end === naturalEnd ||
      (end === reportEnd && reportEnd && reportEnd >= naturalStart);
    return startOk && endOk ? sYm : null;
  })();
  const activeMonthLabel = activeMonth
    ? monthsAvailable.find((m) => m.ym === activeMonth)?.label?.split(" ")[0]?.slice(0, 3).toUpperCase() ||
      activeMonth
    : "Month";

  // Format YYYY-MM-DD → "Jun 17" for compact button label.
  const compactDate = (iso) => {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
  };
  const todayLabel = todayActive && start ? compactDate(start) : "Today";

  // Abbreviated month label e.g. "January 2026" -> "Jan 2026" for the dropdown rows.
  const shortMonthLabel = (label) => {
    if (!label) return label;
    const parts = label.split(" ");
    if (parts.length < 2) return label;
    return `${parts[0].slice(0, 3)} ${parts[parts.length - 1]}`;
  };

  // Compute available quarters from the report's date span, oldest-first then reversed.
  const quartersAvailable = useMemo(() => {
    if (!reportStart || !reportEnd) return [];
    const [ys] = reportStart.split("-").map(Number);
    const [ye] = reportEnd.split("-").map(Number);
    const out = [];
    for (let y = ys; y <= ye; y++) {
      for (let q = 1; q <= 4; q++) {
        const qStart = `${y}-${String((q - 1) * 3 + 1).padStart(2, "0")}-01`;
        const qEnd = lastDayOfMonth(`${y}-${String(q * 3).padStart(2, "0")}`);
        // Keep quarter only if it overlaps with the report range.
        if (qEnd < reportStart || qStart > reportEnd) continue;
        out.push({
          key: `${y}-Q${q}`,
          label: `Q${q} ${y}`,
          start: qStart > reportStart ? qStart : reportStart,
          end: qEnd < reportEnd ? qEnd : reportEnd,
          rawStart: qStart,
          rawEnd: qEnd,
        });
      }
    }
    return out.reverse();
  }, [reportStart, reportEnd]);

  const activeQuarter = quartersAvailable.find(
    (q) => q.start === start && q.end === end
  );

  // Available ODB cycles within the report window.
  const odbCycles = useMemo(
    () => odbCyclesInRange(reportStart, reportEnd),
    [reportStart, reportEnd]
  );
  const activeOdbCycle = odbCycles.find(
    (c) =>
      // Match if the selected range exactly equals the cycle's dispense window
      // (clamped to the report bounds, in case the cycle extends outside).
      start === clampIso(c.dispenseStart, reportStart, reportEnd) &&
      end === clampIso(c.dispenseEnd, reportStart, reportEnd)
  );

  // Available NIHB cycles within the report window — bi-weekly cadence
  // per the published NIHB payment calendar, distinct from ODB cycles.
  const nihbCycles = useMemo(
    () => nihbCyclesInRange(reportStart, reportEnd),
    [reportStart, reportEnd]
  );
  const activeNihbCycle = nihbCycles.find(
    (c) =>
      start === clampIso(c.dispenseStart, reportStart, reportEnd) &&
      end === clampIso(c.dispenseEnd, reportStart, reportEnd)
  );

  const chip =
    "rounded-full px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors";
  const chipIdle =
    "border border-[#E6EBE8] bg-white text-[#3D4F46] hover:bg-[#F7F9F8]";
  const chipActive = "bg-[#1B3B2E] text-white";

  return (
    <div className="px-6 pb-6 pt-2 space-y-5">
      {/* Product scope toggle — moved here from the main dashboard for cleanliness */}
      {onViewModeChange ? (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C746A] mb-2">
            Product scope
          </div>
          <div className="grid grid-cols-2 gap-1.5 p-1 rounded-full bg-[#F0F4F2]">
            <button
              type="button"
              data-testid="view-mode-non-hcv"
              onClick={() => onViewModeChange("non-hcv")}
              className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                viewMode === "non-hcv"
                  ? "bg-[#1B3B2E] text-white shadow-sm"
                  : "text-[#5C746A] hover:bg-white/60"
              }`}
            >
              Non-HCV
            </button>
            <button
              type="button"
              data-testid="view-mode-hcv"
              onClick={() => onViewModeChange("hcv")}
              className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                viewMode === "hcv"
                  ? "bg-[#1B3B2E] text-white shadow-sm"
                  : "text-[#5C746A] hover:bg-white/60"
              }`}
            >
              HCV
            </button>
          </div>
        </div>
      ) : null}

      {/* Hidden date input that powers the Today button's native picker */}
      <input
        ref={singleDayInputRef}
        type="date"
        data-testid="range-today-hidden-input"
        value={todayActive ? start : (today || "")}
        min={reportStart || undefined}
        max={reportEnd || undefined}
        onChange={(e) => {
          const v = e.target.value;
          if (v) setRange(v, v);
        }}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
      {/* Quick options — Today, Month ▼, Quarter ▼, Full Range */}
      <div className="grid grid-cols-2 gap-2" data-testid="range-presets">
        <button
          type="button"
          data-testid="range-preset-today"
          onClick={openSingleDayPicker}
          className={`${chip} ${todayActive ? chipActive : chipIdle} inline-flex items-center justify-center gap-1`}
        >
          <span className="truncate">{todayLabel}</span>
          <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.5} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="range-preset-month"
              className={`${chip} ${
                selectedMonthKeys.size > 0 || activeMonth ? chipActive : chipIdle
              } inline-flex items-center justify-center gap-1`}
            >
              <span className="truncate">
                {selectedMonthKeys.size > 1
                  ? `Months · ${selectedMonthKeys.size}`
                  : activeMonthLabel}
              </span>
              <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            sideOffset={6}
            collisionPadding={16}
            className="min-w-[140px] max-h-[50vh] overflow-y-auto overscroll-contain bg-white border-[#E6EBE8]"
            data-testid="range-month-menu"
          >
            {monthsAvailable.map((m) => {
              const isChecked =
                selectedMonthKeys.size > 0
                  ? selectedMonthKeys.has(m.ym)
                  : activeMonth === m.ym;
              return (
                <DropdownMenuItem
                  key={m.ym}
                  data-testid={`range-month-${m.ym}`}
                  onSelect={(e) => {
                    // Keep dropdown open so multiple months can be picked.
                    e.preventDefault();
                    toggleMonth(m.ym);
                  }}
                  className="text-xs font-medium text-[#1B3B2E] cursor-pointer"
                >
                  <span className="flex-1">{shortMonthLabel(m.label)}</span>
                  {isChecked && (
                    <Check className="h-3.5 w-3.5 text-[#2B5E4A]" strokeWidth={2} />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="range-preset-quarter"
              disabled={quartersAvailable.length === 0}
              className={`${chip} ${
                selectedQuarterKeys.size > 0 || activeQuarter ? chipActive : chipIdle
              } inline-flex items-center justify-center gap-1 disabled:opacity-40`}
            >
              <span className="truncate">
                {selectedQuarterKeys.size > 1
                  ? `Quarters · ${selectedQuarterKeys.size}`
                  : activeQuarter
                    ? activeQuarter.label
                    : "Quarter"}
              </span>
              <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            sideOffset={6}
            collisionPadding={16}
            className="min-w-[140px] max-h-[50vh] overflow-y-auto overscroll-contain bg-white border-[#E6EBE8]"
            data-testid="range-quarter-menu"
          >
            {quartersAvailable.map((q) => {
              const isChecked =
                selectedQuarterKeys.size > 0
                  ? selectedQuarterKeys.has(q.key)
                  : activeQuarter && activeQuarter.key === q.key;
              return (
                <DropdownMenuItem
                  key={q.key}
                  data-testid={`range-quarter-${q.key}`}
                  onSelect={(e) => {
                    e.preventDefault();
                    toggleQuarter(q, quartersAvailable);
                  }}
                  className="text-xs font-medium text-[#1B3B2E] cursor-pointer"
                >
                  <span className="flex-1">{q.label}</span>
                  {isChecked && (
                    <Check className="h-3.5 w-3.5 text-[#2B5E4A]" strokeWidth={2} />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          data-testid="range-preset-full"
          onClick={() => setRange(reportStart, reportEnd)}
          className={`${chip} ${fullActive ? chipActive : chipIdle}`}
        >
          Full Range
        </button>
      </div>

      {/* ODB Cycle quick-select — Ontario government reimbursement cadence.
          Multi-select: each tap toggles a cycle. The Start/End range
          encompasses the union of all selected cycles (min start → max end).
          Tapping does NOT close the dropdown so users can tick several
          cycles in one session. */}
      {odbCycles.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="range-preset-odb"
              className={`w-full ${chip} ${
                selectedOdbKeys.size > 0 || activeOdbCycle ? chipActive : chipIdle
              } inline-flex items-center justify-center gap-1.5`}
            >
              <span className="truncate">
                {selectedOdbKeys.size > 1
                  ? `ODB · ${selectedOdbKeys.size} cycles`
                  : activeOdbCycle
                    ? `ODB · Pays ${formatPayDate(activeOdbCycle.payDate)}`
                    : "ODB Cycle"}
              </span>
              <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            sideOffset={6}
            collisionPadding={16}
            className="min-w-[240px] max-h-[50vh] overflow-y-auto overscroll-contain bg-white border-[#E6EBE8]"
            data-testid="range-odb-menu"
          >
            {odbCycles.map((c) => {
              // A cycle is "ticked" either via the multi-select Set or, in
              // single-select mode (Set empty), by exact start/end match.
              const isChecked =
                selectedOdbKeys.size > 0
                  ? selectedOdbKeys.has(c.key)
                  : activeOdbCycle && activeOdbCycle.key === c.key;
              return (
                <DropdownMenuItem
                  key={c.key}
                  data-testid={`range-odb-${c.key}`}
                  onSelect={(e) => {
                    // Keep dropdown open so multiple cycles can be picked.
                    e.preventDefault();
                    toggleOdbCycle(c, odbCycles);
                  }}
                  className="text-xs font-medium text-[#1B3B2E] cursor-pointer flex-col items-start gap-0.5 py-2"
                >
                  {/* Primary label = PAY DATE. This matches the headline
                      on each ODB cycle card in the dashboard so users
                      never see a mismatch between what they ticked here
                      and what the card is titled ("I picked Jun cycle
                      but the card says Jul 15?!"). Dispensing dates are
                      the subtitle since they're just supporting detail. */}
                  <div className="flex items-center w-full gap-2">
                    <span className="flex-1 truncate">
                      Pays {formatPayDate(c.payDate)}
                    </span>
                    {isChecked ? (
                      <Check className="h-3.5 w-3.5 text-[#2B5E4A] shrink-0" strokeWidth={2} />
                    ) : null}
                  </div>
                  <div className="text-[10px] font-normal text-[#8A9C93] tracking-normal normal-case">
                    {c.label}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {/* NIHB Cycle quick-select — bi-weekly federal reimbursement
          cadence. Windows do NOT align with ODB, so each NIHB cycle
          gets its own tile even if the calendar dates seem to
          overlap. Same multi-select behaviour as the ODB dropdown. */}
      {nihbCycles.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="range-preset-nihb"
              className={`w-full ${chip} ${
                selectedNihbKeys.size > 0 || activeNihbCycle ? chipActive : chipIdle
              } inline-flex items-center justify-center gap-1.5`}
            >
              <span className="truncate">
                {selectedNihbKeys.size > 1
                  ? `NIHB · ${selectedNihbKeys.size} cycles`
                  : activeNihbCycle
                    ? `NIHB · Pays ${formatPayDate(activeNihbCycle.payDate)}`
                    : "NIHB Cycle"}
              </span>
              <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            sideOffset={6}
            collisionPadding={16}
            className="min-w-[240px] max-h-[50vh] overflow-y-auto overscroll-contain bg-white border-[#E6EBE8]"
            data-testid="range-nihb-menu"
          >
            {nihbCycles.map((c) => {
              const isChecked =
                selectedNihbKeys.size > 0
                  ? selectedNihbKeys.has(c.key)
                  : activeNihbCycle && activeNihbCycle.key === c.key;
              return (
                <DropdownMenuItem
                  key={c.key}
                  data-testid={`range-nihb-${c.key}`}
                  onSelect={(e) => {
                    e.preventDefault();
                    toggleNihbCycle(c, nihbCycles);
                  }}
                  className="text-xs font-medium text-[#1B3B2E] cursor-pointer flex-col items-start gap-0.5 py-2"
                >
                  {/* Same rationale as the ODB dropdown — pay date up
                      top so it matches the NIHB card headline. */}
                  <div className="flex items-center w-full gap-2">
                    <span className="flex-1 truncate">
                      Pays {formatPayDate(c.payDate)}
                    </span>
                    {isChecked ? (
                      <Check className="h-3.5 w-3.5 text-[#2B5E4A] shrink-0" strokeWidth={2} />
                    ) : null}
                  </div>
                  <div className="text-[10px] font-normal text-[#8A9C93] tracking-normal normal-case">
                    {c.label}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <div className="space-y-2">
        <label className="flex items-center gap-3 rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] px-3.5 py-2.5 focus-within:border-[#2B5E4A] focus-within:bg-white transition-colors">
          <CalendarDays className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={1.75} />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C746A] w-12">
            Start
          </span>
          <input
            type="date"
            data-testid="range-start-input"
            value={start}
            min={reportStart || undefined}
            max={reportEnd || undefined}
            onChange={(e) => {
              setStart(e.target.value);
              setSelectedOdbKeys(new Set());
              setSelectedNihbKeys(new Set());
            }}
            className="flex-1 bg-transparent text-sm font-mono-data text-[#1B3B2E] focus:outline-none w-full text-right"
          />
        </label>
        <label className="flex items-center gap-3 rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] px-3.5 py-2.5 focus-within:border-[#2B5E4A] focus-within:bg-white transition-colors">
          <CalendarDays className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={1.75} />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C746A] w-12">
            End
          </span>
          <input
            type="date"
            data-testid="range-end-input"
            value={end}
            min={reportStart || undefined}
            max={reportEnd || undefined}
            onChange={(e) => {
              setEnd(e.target.value);
              setSelectedOdbKeys(new Set());
              setSelectedNihbKeys(new Set());
            }}
            className="flex-1 bg-transparent text-sm font-mono-data text-[#1B3B2E] focus:outline-none w-full text-right"
          />
        </label>
      </div>

      {invalid || outOfBounds ? (
        <p className="text-xs text-[#B65A45]">
          {invalid
            ? "Start date must be on or before the end date."
            : "Range must stay within the uploaded report's bounds."}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          data-testid="range-clear"
          onClick={onClear}
          className="text-sm font-medium text-[#5C746A] hover:text-[#1B3B2E] transition-colors"
        >
          Reset
        </button>
        <button
          type="button"
          data-testid="range-apply"
          disabled={invalid || outOfBounds}
          onClick={() => {
            // Tag the range with which taxonomy the user selected from
            // (odb / nihb / both / full / null) so the dashboard can
            // hide/show relevant cycle sections and pick the right
            // window when drilling.
            let source = null;
            const hasOdb = selectedOdbKeys.size > 0;
            const hasNihb = selectedNihbKeys.size > 0;
            if (hasOdb && hasNihb) source = "both";
            else if (hasOdb) source = "odb";
            else if (hasNihb) source = "nihb";
            else if (start === reportStart && end === reportEnd) source = "full";
            // Pass along the specific keys the user selected so the
            // dashboard can restrict cycle-card rendering to exactly
            // those cycles — not every cycle overlapping the range.
            onApply({
              start,
              end,
              source,
              odbCycleKeys: Array.from(selectedOdbKeys),
              nihbCycleKeys: Array.from(selectedNihbKeys),
            });
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1B3B2E] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#2B5E4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="h-4 w-4" strokeWidth={2} />
          Apply
        </button>
      </div>
    </div>
  );
}
